import { useCallback, useEffect, useState } from "react";
import { api, money, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { DataTable, PayDialog } from "@/components/DataTable";

const SOURCE_LABELS = {
  VehiclePurchase: "Araç Alışı", VehicleSale: "Araç Satışı", VehicleExpense: "Araç Masrafı",
  ProjectCost: "Proje Maliyeti", ProjectSale: "Bağımsız Bölüm Satışı", ExcavationCost: "Hafriyat Gideri",
  ExcavationRevenue: "Hafriyat Sözleşmesi", GeneralExpense: "Genel Gider", GeneralIncome: "Genel Gelir",
};
export const sourceLabel = (s) => SOURCE_LABELS[s] || s;

/** Ledger rows for any filter (vehicle_id, project_id, excavation_job_id, area...). Every amount = one source record. */
export function TxPanel({ params, reloadKey, onChange, showSums = true }) {
  const { can } = useAuth();
  const [data, setData] = useState({ items: [], total: 0, sums: {} });
  const [skip, setSkip] = useState(0);
  const limit = 50;
  const pkey = JSON.stringify(params);

  const load = useCallback(() => {
    api.get("/transactions", { params: { ...params, skip, limit } }).then((r) => setData(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pkey, skip, reloadKey]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSkip(0); }, [pkey]);

  const done = () => { load(); onChange && onChange(); };
  const columns = [
    { key: "transaction_date", label: "Tarih", render: (t) => formatDate(t.transaction_date) },
    { key: "source_type", label: "Kaynak", render: (t) => <span className="text-xs">{sourceLabel(t.source_type)}</span> },
    { key: "business_area", label: "Alan" },
    { key: "description", label: "Açıklama", className: "max-w-[280px] truncate", render: (t) => <span title={t.description}>{t.description}</span> },
    { key: "party_name", label: "Cari" },
    { key: "direction", label: "Yön", render: (t) => <StatusBadge value={t.direction} /> },
    { key: "amount", label: "Tutar", align: "right", render: (t) => <b>{money(t.amount)}</b> },
    { key: "paid_amount", label: "Ödenen", align: "right", render: (t) => money(t.paid_amount) },
    { key: "remaining", label: "Kalan", align: "right", render: (t) => money(t.remaining) },
    { key: "display_status", label: "Durum", render: (t) => <StatusBadge value={t.display_status} /> },
    { key: "act", label: "", render: (t) => t.remaining > 0 && can("finans") && (
      <PayDialog tx={t} remaining={t.remaining} label={t.direction === "Gelir" ? "Tahsil Et" : "Öde"} onDone={done} />) },
  ];
  const s = data.sums || {};
  return (
    <div className="space-y-3" data-testid="tx-panel">
      {showSums && (
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm" data-testid="tx-sums">
          <span>Gelir: <b className="text-emerald-600">{money(s.Gelir?.amount || 0)}</b></span>
          <span>Gider: <b className="text-rose-600">{money(s.Gider?.amount || 0)}</b></span>
          <span>Tahsil edilen: <b>{money(s.Gelir?.paid || 0)}</b></span>
          <span>Ödenen: <b>{money(s.Gider?.paid || 0)}</b></span>
          <span className="text-muted-foreground">{data.total} işlem</span>
        </div>
      )}
      <DataTable columns={columns} rows={data.items} testIdPrefix="tx-row" empty="Finansal hareket yok" />
      {data.total > limit && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <Button size="sm" variant="outline" disabled={skip === 0} onClick={() => setSkip(skip - limit)} data-testid="tx-prev">Önceki</Button>
          <span>{skip + 1}-{Math.min(skip + limit, data.total)} / {data.total}</span>
          <Button size="sm" variant="outline" disabled={skip + limit >= data.total} onClick={() => setSkip(skip + limit)} data-testid="tx-next">Sonraki</Button>
        </div>
      )}
    </div>
  );
}
